// Single source of truth for hreflang + canonical link tags.
//
// Every public page must emit a self-referencing hreflang for its own
// language plus an x-default, otherwise Semrush/Google report
// "No self-referencing hreflang". Rather than hand-writing those three or
// four <link> tags on each route, routes call `canonicalAndAlternates()`
// with their English path and get the whole set back, derived from the
// English/Spanish pairs already declared in ES_PAGES.

import { ES_PAGES, SITE, type EsPage } from "./es-pages";

export interface HeadLink {
  rel: string;
  href: string;
  hrefLang?: string;
}

/** English path -> Spanish path, built from the ES_PAGES pair table. */
const EN_TO_ES: Record<string, string> = {};
for (const page of Object.values(ES_PAGES) as EsPage[]) {
  // Spanish-only pages (noAlternate) have no English twin to pair with.
  if (page.noAlternate) continue;
  if (!EN_TO_ES[page.en]) EN_TO_ES[page.en] = page.es;
}

/** Absolute URL for a site-relative path. The home page keeps its slash. */
export function absoluteUrl(path: string) {
  if (path === "/") return `${SITE}/`;
  return `${SITE}${path.startsWith("/") ? path : `/${path}`}`;
}

/** True when the given English path has a translated Spanish counterpart. */
export function hasSpanishTwin(enPath: string) {
  return Boolean(EN_TO_ES[enPath]);
}

/**
 * hreflang alternates for an English page. Returns an empty array when the
 * page has no translation: a lone hreflang on a monolingual page is itself
 * an error, so we emit nothing rather than a dangling x-default.
 */
export function alternateLinks(enPath: string): HeadLink[] {
  const esPath = EN_TO_ES[enPath];
  if (!esPath) return [];
  const enUrl = absoluteUrl(enPath);
  return [
    { rel: "alternate", hrefLang: "en", href: enUrl },
    { rel: "alternate", hrefLang: "es", href: absoluteUrl(esPath) },
    { rel: "alternate", hrefLang: "x-default", href: enUrl },
  ];
}

/**
 * Canonical + hreflang set for an English route. This is what route `head()`
 * spreads into its `links` array, e.g.
 *
 *   links: [...canonicalAndAlternates("/services/sleeve")]
 */
export function canonicalAndAlternates(enPath: string): HeadLink[] {
  return [
    { rel: "canonical", href: absoluteUrl(enPath) },
    ...alternateLinks(enPath),
  ];
}

/**
 * Patient-story pages are generated from a slug, so their pair is derived
 * rather than listed in ES_PAGES. Both languages use the same slug.
 */
export function storyCanonicalAndAlternates(slug: string, lang: "en" | "es"): HeadLink[] {
  const enUrl = `${SITE}/results/story/${slug}`;
  const esUrl = `${SITE}/es/antes-y-despues/historia/${slug}`;
  const self = lang === "en" ? enUrl : esUrl;
  return [
    { rel: "canonical", href: self },
    { rel: "alternate", hrefLang: "en", href: enUrl },
    { rel: "alternate", hrefLang: "es", href: esUrl },
    { rel: "alternate", hrefLang: "x-default", href: enUrl },
  ];
}
